import { useState, useEffect } from 'react';
import { casesApi, usersApi, clientsApi } from '../lib/api';
import { generatePremiseTemplate, isAutoGeneratedPremise, getInvestigatedFullName } from '../lib/premiseTemplates';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, FolderOpen, User, Car, MapPin, Home, Calendar, AlertCircle, Camera, Upload, Tag, Building, CreditCard, Search, UserPlus } from 'lucide-react';

const CASE_TYPES = [
    { value: 'infedelta_coniugale', label: 'Infedeltà coniugale' },
    { value: 'concorrenza_sleale', label: 'Concorrenza sleale' },
    { value: 'falsa_malattia', label: 'Falsa malattia' },
    { value: 'abuso_104', label: 'Abuso 104' },
    { value: 'altro', label: 'Altro' }
];

const GENDER_OPTIONS = [
    { value: 'M', label: 'Maschile' },
    { value: 'F', label: 'Femminile' }
];

export default function CreateCaseModal({ agents, onClose, onCreated }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Protocol number
    const [protocolNumber, setProtocolNumber] = useState('');
    
    // Case type
    const [caseType, setCaseType] = useState('');
    
    // Clients list and selection
    const [clients, setClients] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [isNewClient, setIsNewClient] = useState(true);
    
    // Client info
    const [clientName, setClientName] = useState('');
    const [clientAddress, setClientAddress] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [premises, setPremises] = useState('');
    
    // Investigated person
    const [investigatedName, setInvestigatedName] = useState('');
    const [investigatedGender, setInvestigatedGender] = useState('');
    const [investigatedVehicle, setInvestigatedVehicle] = useState('');
    const [investigatedBirthDate, setInvestigatedBirthDate] = useState('');
    const [investigatedBirthPlace, setInvestigatedBirthPlace] = useState('');
    const [investigatedFiscalCode, setInvestigatedFiscalCode] = useState('');
    const [investigatedAddress, setInvestigatedAddress] = useState('');
    const [investigatedDomicile, setInvestigatedDomicile] = useState('');
    const [investigatedPhoto, setInvestigatedPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    // Luogo di lavoro
    const [workplaceCompany, setWorkplaceCompany] = useState('');
    const [workplaceAddress, setWorkplaceAddress] = useState('');
    // Social
    const [socialFacebook, setSocialFacebook] = useState('');
    const [socialInstagram, setSocialInstagram] = useState('');
    const [socialTiktok, setSocialTiktok] = useState('');
    const [socialLinkedin, setSocialLinkedin] = useState('');
    const [socialAltro, setSocialAltro] = useState('');
    
    // Additional info
    const [additionalInfo, setAdditionalInfo] = useState('');
    
    // Assigned agents
    const [selectedAgents, setSelectedAgents] = useState([]);

    // Carica lista clienti all'avvio
    useEffect(() => {
        loadClients();
    }, []);

    // Auto-compila "premessa" in base al tipo indagine + nome/genere indagato.
    // Sovrascrive solo se il campo è vuoto oppure contiene un template
    // auto-generato precedente (non cancella modifiche manuali dell'utente).
    useEffect(() => {
        const suggested = generatePremiseTemplate(caseType, investigatedName, investigatedGender);
        if (!suggested) return;
        if (premises.trim() === '' || isAutoGeneratedPremise(premises, investigatedName)) {
            setPremises(suggested);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseType, investigatedName, investigatedGender]);

    const loadClients = async () => {
        try {
            const response = await clientsApi.list();
            setClients(response.data);
        } catch (err) {
            console.error('Errore caricamento clienti:', err);
        }
    };

    // Filtra clienti in base alla ricerca
    const filteredClients = clients.filter(c => 
        c.name.toLowerCase().includes(clientSearch.toLowerCase())
    );

    // Seleziona un cliente esistente
    const selectClient = (client) => {
        setSelectedClientId(client.id);
        setClientName(client.name);
        setClientAddress(client.address || '');
        setClientEmail(client.email || '');
        setClientSearch(client.name);
        setShowClientDropdown(false);
        setIsNewClient(false);
    };

    // Crea nuovo cliente
    const startNewClient = () => {
        setSelectedClientId(null);
        setClientName(clientSearch);
        setClientAddress('');
        setClientEmail('');
        setIsNewClient(true);
        setShowClientDropdown(false);
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setInvestigatedPhoto(file);
            // Crea preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setInvestigatedPhoto(null);
        setPhotoPreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!protocolNumber) {
            setError('Il numero di protocollo è obbligatorio');
            return;
        }
        if (!clientName) {
            setError('Il nome del cliente è obbligatorio');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            // Prima crea il caso
            const response = await casesApi.create({
                protocol_number: protocolNumber,
                case_type: caseType,
                client_id: selectedClientId,  // ID cliente esistente (null se nuovo)
                client_name: clientName,
                client_address: clientAddress,
                client_email: clientEmail,
                premises: premises,
                investigated: {
                    name: investigatedName,
                    gender: investigatedGender,
                    vehicle: investigatedVehicle,
                    birth_date: investigatedBirthDate,
                    birth_place: investigatedBirthPlace,
                    fiscal_code: investigatedFiscalCode.toUpperCase(),
                    address: investigatedAddress,
                    domicile: investigatedDomicile,
                    workplace_company: workplaceCompany,
                    workplace_address: workplaceAddress,
                    social: {
                        facebook: socialFacebook,
                        instagram: socialInstagram,
                        tiktok: socialTiktok,
                        linkedin: socialLinkedin,
                        altro: socialAltro
                    }
                },
                additional_info: additionalInfo,
                assigned_agents: selectedAgents
            });
            
            // Se c'è una foto, caricala
            if (investigatedPhoto && response.data.id) {
                try {
                    await casesApi.uploadInvestigatedPhoto(response.data.id, investigatedPhoto);
                } catch (photoErr) {
                    console.error('Errore upload foto:', photoErr);
                    // Non bloccare la creazione del caso se la foto fallisce
                }
            }
            
            onCreated(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nella creazione del caso');
        } finally {
            setLoading(false);
        }
    };

    const toggleAgent = (agentId) => {
        setSelectedAgents(prev => 
            prev.includes(agentId)
                ? prev.filter(id => id !== agentId)
                : [...prev, agentId]
        );
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 p-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                        <h2 className="font-heading text-lg font-bold text-white">Nuovo Caso Investigativo</h2>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-white/60 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 text-sm">{error}</span>
                        </div>
                    )}

                    {/* Numero Protocollo e Tipologia */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 bg-[#00FF41]/5 border border-[#00FF41]/30 p-4">
                            <Label className="text-[#00FF41] uppercase text-xs font-bold">Numero Protocollo *</Label>
                            <div className="flex items-center gap-2">
                                <span className="text-[#00FF41] font-mono font-bold text-lg">PROT-</span>
                                <Input
                                    value={protocolNumber}
                                    onChange={(e) => setProtocolNumber(e.target.value)}
                                    placeholder="2024/001"
                                    required
                                    data-testid="protocol-number-input"
                                    className="bg-black border-[#00FF41]/50 text-white font-mono focus:border-[#00FF41] rounded-none flex-1"
                                />
                            </div>
                            <p className="text-white/40 text-xs">Inserisci il numero di protocollo che identificherà il caso</p>
                        </div>
                        
                        <div className="space-y-2 bg-[#00FF41]/5 border border-[#00FF41]/30 p-4">
                            <Label className="text-[#00FF41] uppercase text-xs font-bold flex items-center gap-1">
                                <Tag className="w-3 h-3" /> Tipologia
                            </Label>
                            <Select value={caseType} onValueChange={setCaseType}>
                                <SelectTrigger 
                                    data-testid="case-type-select"
                                    className="bg-black border-[#00FF41]/50 text-white focus:border-[#00FF41] rounded-none"
                                >
                                    <SelectValue placeholder="Seleziona tipologia..." />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/20">
                                    {CASE_TYPES.map((type) => (
                                        <SelectItem 
                                            key={type.value} 
                                            value={type.value}
                                            className="text-white hover:bg-white/10"
                                        >
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-white/40 text-xs">Seleziona la categoria dell'indagine</p>
                        </div>
                    </div>

                    {/* Client Info Section */}
                    <div className="space-y-4">
                        <h3 className="font-heading text-white flex items-center gap-2 border-b border-white/10 pb-2">
                            <User className="w-4 h-4 text-[#00FF41]" />
                            Informazioni Cliente
                            {!isNewClient && selectedClientId && (
                                <span className="text-xs bg-[#00FF41]/20 text-[#00FF41] px-2 py-0.5 ml-2">
                                    Cliente esistente
                                </span>
                            )}
                        </h3>
                        
                        {/* Ricerca cliente */}
                        <div className="relative">
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                <Search className="w-3 h-3" /> Cerca o inserisci cliente *
                            </Label>
                            <Input
                                value={clientSearch}
                                onChange={(e) => {
                                    setClientSearch(e.target.value);
                                    setShowClientDropdown(true);
                                    if (!e.target.value) {
                                        setSelectedClientId(null);
                                        setClientName('');
                                        setClientAddress('');
                                        setClientEmail('');
                                    }
                                }}
                                onFocus={() => setShowClientDropdown(true)}
                                placeholder="Inizia a digitare per cercare..."
                                data-testid="client-search-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                            />
                            
                            {/* Dropdown risultati */}
                            {showClientDropdown && clientSearch && (
                                <div className="absolute z-20 w-full mt-1 bg-[#0A0A0A] border border-white/20 max-h-60 overflow-y-auto">
                                    {filteredClients.length > 0 ? (
                                        <>
                                            {filteredClients.map(client => (
                                                <button
                                                    key={client.id}
                                                    type="button"
                                                    onClick={() => selectClient(client)}
                                                    className="w-full text-left px-3 py-2 hover:bg-[#00FF41]/10 border-b border-white/10 last:border-b-0"
                                                >
                                                    <div className="text-white font-medium">{client.name}</div>
                                                    {client.email && (
                                                        <div className="text-white/50 text-xs">{client.email}</div>
                                                    )}
                                                    <div className="text-white/30 text-xs">
                                                        {client.cases_count} caso/i
                                                    </div>
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={startNewClient}
                                                className="w-full text-left px-3 py-2 hover:bg-[#00FF41]/10 bg-[#00FF41]/5 flex items-center gap-2"
                                            >
                                                <UserPlus className="w-4 h-4 text-[#00FF41]" />
                                                <span className="text-[#00FF41]">Crea nuovo cliente "{clientSearch}"</span>
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={startNewClient}
                                            className="w-full text-left px-3 py-2 hover:bg-[#00FF41]/10 flex items-center gap-2"
                                        >
                                            <UserPlus className="w-4 h-4 text-[#00FF41]" />
                                            <span className="text-[#00FF41]">Crea nuovo cliente "{clientSearch}"</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        {/* Campi cliente (visibili quando selezionato o nuovo) */}
                        {(clientName || selectedClientId) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-black/30 border border-white/10">
                                <div>
                                    <Label className="text-white/60 uppercase text-xs">Nome Cliente</Label>
                                    <Input
                                        value={clientName}
                                        onChange={(e) => setClientName(e.target.value)}
                                        placeholder="Nome del cliente"
                                        required
                                        disabled={!isNewClient && selectedClientId}
                                        data-testid="client-name-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 disabled:opacity-50"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase text-xs">Email</Label>
                                    <Input
                                        type="email"
                                        value={clientEmail}
                                        onChange={(e) => setClientEmail(e.target.value)}
                                        placeholder="email@esempio.it"
                                        disabled={!isNewClient && selectedClientId}
                                        data-testid="client-email-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 disabled:opacity-50"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label className="text-white/60 uppercase text-xs">Indirizzo</Label>
                                    <Input
                                        value={clientAddress}
                                        onChange={(e) => setClientAddress(e.target.value)}
                                        placeholder="Via, CAP, Città"
                                        disabled={!isNewClient && selectedClientId}
                                        data-testid="client-address-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 disabled:opacity-50"
                                    />
                                </div>
                                {!isNewClient && selectedClientId && (
                                    <div className="md:col-span-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedClientId(null);
                                                setIsNewClient(true);
                                            }}
                                            className="text-xs text-[#00FF41] hover:underline"
                                        >
                                            Modifica dati o crea nuovo cliente
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Premises */}
                    <div className="space-y-2">
                        <Label className="text-white/60 uppercase text-xs">Premessa</Label>
                        <Textarea
                            value={premises}
                            onChange={(e) => setPremises(e.target.value)}
                            placeholder="Descrivi il contesto e le motivazioni dell'indagine..."
                            rows={4}
                            data-testid="premises-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none"
                        />
                    </div>

                    {/* Investigated Person Section */}
                    <div className="space-y-4">
                        <h3 className="font-heading text-white flex items-center gap-2 border-b border-white/10 pb-2">
                            <User className="w-4 h-4 text-[#00FF41]" />
                            Informazioni Indagato
                        </h3>
                        
                        {/* Foto Indagato */}
                        <div>
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1 mb-2">
                                <Camera className="w-3 h-3" /> Foto Indagato
                            </Label>
                            <div className="flex items-center gap-4">
                                {photoPreview ? (
                                    <div className="relative group">
                                        <div className="w-20 h-20 border border-white/20 overflow-hidden">
                                            <img
                                                src={photoPreview}
                                                alt="Preview"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={removePhoto}
                                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-20 h-20 border border-dashed border-white/30 flex items-center justify-center bg-black/30">
                                        <Camera className="w-6 h-6 text-white/30" />
                                    </div>
                                )}
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        className="hidden"
                                        data-testid="investigated-photo-input"
                                    />
                                    <div className="px-3 py-2 bg-[#00FF41] text-black text-xs font-bold uppercase hover:bg-[#00CC33] transition-all flex items-center gap-1">
                                        <Upload className="w-3 h-3" />
                                        {photoPreview ? 'CAMBIA' : 'CARICA'}
                                    </div>
                                </label>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <User className="w-3 h-3" /> Nome Indagato
                                </Label>
                                <Input
                                    value={investigatedName}
                                    onChange={(e) => setInvestigatedName(e.target.value)}
                                    placeholder="Nome completo"
                                    data-testid="investigated-name-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs">Sesso</Label>
                                <Select value={investigatedGender} onValueChange={setInvestigatedGender}>
                                    <SelectTrigger className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1">
                                        <SelectValue placeholder="Seleziona..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0A0A0A] border-white/20">
                                        {GENDER_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-white/10">
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> Data di Nascita
                                </Label>
                                <Input
                                    type="date"
                                    value={investigatedBirthDate}
                                    onChange={(e) => setInvestigatedBirthDate(e.target.value)}
                                    data-testid="investigated-birth-date-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> Luogo di Nascita
                                </Label>
                                <Input
                                    value={investigatedBirthPlace}
                                    onChange={(e) => setInvestigatedBirthPlace(e.target.value)}
                                    placeholder="Città di nascita"
                                    data-testid="investigated-birth-place-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" /> Codice Fiscale
                                </Label>
                                <Input
                                    value={investigatedFiscalCode}
                                    onChange={(e) => setInvestigatedFiscalCode(e.target.value.toUpperCase())}
                                    placeholder="RSSMRA80A01H501U"
                                    maxLength={16}
                                    data-testid="investigated-fiscal-code-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono uppercase"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> Indirizzo Residenza
                                </Label>
                                <Input
                                    value={investigatedAddress}
                                    onChange={(e) => setInvestigatedAddress(e.target.value)}
                                    placeholder="Via, CAP, Città"
                                    data-testid="investigated-address-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <Home className="w-3 h-3" /> Domicilio
                                </Label>
                                <Input
                                    value={investigatedDomicile}
                                    onChange={(e) => setInvestigatedDomicile(e.target.value)}
                                    placeholder="Se diverso dalla residenza"
                                    data-testid="investigated-domicile-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                    <Car className="w-3 h-3" /> Automezzo
                                </Label>
                                <Input
                                    value={investigatedVehicle}
                                    onChange={(e) => setInvestigatedVehicle(e.target.value)}
                                    placeholder="Marca, modello, targa"
                                    data-testid="investigated-vehicle-input"
                                    className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                />
                            </div>
                        </div>
                        
                        {/* Luogo di Lavoro */}
                        <div className="space-y-3 pt-3 border-t border-white/10">
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                <Building className="w-3 h-3" /> Luogo di Lavoro
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white/40 text-xs">Azienda</Label>
                                    <Input
                                        value={workplaceCompany}
                                        onChange={(e) => setWorkplaceCompany(e.target.value)}
                                        placeholder="Nome azienda"
                                        data-testid="workplace-company-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs">Indirizzo</Label>
                                    <Input
                                        value={workplaceAddress}
                                        onChange={(e) => setWorkplaceAddress(e.target.value)}
                                        placeholder="Indirizzo sede lavoro"
                                        data-testid="workplace-address-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Social Network */}
                        <div className="space-y-3 pt-3 border-t border-white/10">
                            <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                                Social Network
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-blue-600 rounded text-white text-[10px] flex items-center justify-center font-bold">f</span>
                                        Facebook
                                    </Label>
                                    <Input
                                        value={socialFacebook}
                                        onChange={(e) => setSocialFacebook(e.target.value)}
                                        placeholder="username o URL"
                                        data-testid="social-facebook-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 rounded text-white text-[10px] flex items-center justify-center font-bold">I</span>
                                        Instagram
                                    </Label>
                                    <Input
                                        value={socialInstagram}
                                        onChange={(e) => setSocialInstagram(e.target.value)}
                                        placeholder="@username o URL"
                                        data-testid="social-instagram-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-black border border-white/30 rounded text-white text-[10px] flex items-center justify-center font-bold">T</span>
                                        TikTok
                                    </Label>
                                    <Input
                                        value={socialTiktok}
                                        onChange={(e) => setSocialTiktok(e.target.value)}
                                        placeholder="@username o URL"
                                        data-testid="social-tiktok-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white/40 text-xs flex items-center gap-1">
                                        <span className="w-4 h-4 bg-blue-700 rounded text-white text-[10px] flex items-center justify-center font-bold">in</span>
                                        LinkedIn
                                    </Label>
                                    <Input
                                        value={socialLinkedin}
                                        onChange={(e) => setSocialLinkedin(e.target.value)}
                                        placeholder="username o URL"
                                        data-testid="social-linkedin-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label className="text-white/40 text-xs">Altro (altri social o note)</Label>
                                    <Input
                                        value={socialAltro}
                                        onChange={(e) => setSocialAltro(e.target.value)}
                                        placeholder="Altri profili social, note..."
                                        data-testid="social-altro-input"
                                        className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-2">
                        <Label className="text-white/60 uppercase text-xs">Informazioni Aggiuntive</Label>
                        <Textarea
                            value={additionalInfo}
                            onChange={(e) => setAdditionalInfo(e.target.value)}
                            placeholder="Altre informazioni rilevanti..."
                            rows={3}
                            data-testid="additional-info-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none"
                        />
                    </div>

                    {/* Assign Agents */}
                    {agents.length > 0 && (
                        <div className="space-y-4">
                            <h3 className="font-heading text-white flex items-center gap-2 border-b border-white/10 pb-2">
                                <User className="w-4 h-4 text-[#00FF41]" />
                                Assegna Agenti
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {agents.map((agent) => (
                                    <label
                                        key={agent.id}
                                        className={`flex items-center gap-3 p-3 border cursor-pointer transition-all ${
                                            selectedAgents.includes(agent.id)
                                                ? 'border-[#00FF41] bg-[#00FF41]/10'
                                                : 'border-white/10 hover:border-white/30'
                                        }`}
                                    >
                                        <Checkbox
                                            checked={selectedAgents.includes(agent.id)}
                                            onCheckedChange={() => toggleAgent(agent.id)}
                                            className="border-white/40 data-[state=checked]:bg-[#00FF41] data-[state=checked]:border-[#00FF41]"
                                        />
                                        <span className="text-white text-sm">{agent.full_name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-white/20 text-white hover:bg-white/10 rounded-none"
                        >
                            ANNULLA
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-case-btn"
                            className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                        >
                            {loading ? 'CREAZIONE...' : 'CREA CASO'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
